import React, {useState, useEffect, useCallback, memo} from 'react';
import {Plus, Trash2, ChevronUp, ChevronDown, MoreVertical, Loader2} from 'lucide-react';
import useApi from "../../../utils/useApi";
import {Link, useNavigate, useParams} from "react-router-dom";
import LoadingScreen from "../../LoadingScreen/LoadingScreen";
import Button from "../../UI/Button";

// Memoized FormField component
const FormField = memo(({
                            field,
                            onFieldUpdate,
                            onDeleteField,
                            onMoveField,
                            index,
                            isFirst,
                            isLast,
                            documentTypes
                        }) => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [fieldState, setFieldState] = useState(field);
    const [showOptions, setShowOptions] = useState(
        ['select', 'radio', 'checkbox'].includes(field.type)
    );

    const handleFieldChange = useCallback((updates) => {
        const updatedField = { ...fieldState, ...updates };
        setFieldState(updatedField);
        onFieldUpdate(index, updatedField);
    }, [fieldState, index, onFieldUpdate]);

    const addDocument = useCallback(() => {
        const newDocuments = [...(fieldState.documents || []), { id: null, name: '' }];
        handleFieldChange({ documents: newDocuments });
    }, [fieldState.documents, handleFieldChange]);

    const removeDocument = useCallback((docIndex, docId, fieldState) => {
        if (!docId) {
            // If no docId, just remove from documents array without tracking
            const newDocuments = [...fieldState.documents];
            newDocuments.splice(docIndex, 1);
            handleFieldChange({ documents: newDocuments });
            return;
        }

        // Create the removed document object with the correct structure
        const removedDocument = {
            doc_id: docId,
            field_id: fieldState.id
        };

        // Add to deleted documents first
        const newDeletedDocuments = [...(fieldState.deletedDocuments || []), removedDocument];

        // Remove from current documents array
        const newDocuments = [...fieldState.documents];
        newDocuments.splice(docIndex, 1);

        // Update both arrays in a single handleFieldChange call
        handleFieldChange({
                              documents: newDocuments,
                              deletedDocuments: newDeletedDocuments
                          });
    }, [fieldState, handleFieldChange]);

    const updateDocument = useCallback((docIndex, documentId, field) => {
        const selectedDoc = documentTypes.find(dt => dt.id === parseInt(documentId));
        if (selectedDoc) {
            const newDocuments = [...fieldState.documents];
            newDocuments[docIndex] = {
                id: selectedDoc.id,
                document_id: documentId,
                field_id: field.id,
                name: selectedDoc.name,
                description: selectedDoc.description
            };
            handleFieldChange({ documents: newDocuments });
        }
    }, [documentTypes, fieldState.documents, handleFieldChange]);

    const addOption = useCallback(() => {
        const newOptions = [...(fieldState.options || []), { value: '', label: '' }];
        handleFieldChange({ options: newOptions });
    }, [fieldState.options, handleFieldChange]);

    const removeOption = useCallback((optIndex, optId) => {
        if (!optId) {
            const newOptions = [...fieldState.options];
            newOptions.splice(optIndex, 1);
            handleFieldChange({ options: newOptions });
            return;
        }

        // Create the removed document object with the correct structure
        const removedOption = {
            opt_id: optId,
            field_id: fieldState.id
        };

        // Add to deleted documents first
        const newDeletedOptions = [...(fieldState.deletedOptions || []), removedOption];

        const newOptions = [...fieldState.options];
        newOptions.splice(optIndex, 1);

        handleFieldChange({
                              options: newOptions,
                              deletedOptions: newDeletedOptions
                          });
    }, [fieldState, handleFieldChange]);

    const updateOption = useCallback((optIndex, updates) => {
        const newOptions = [...fieldState.options];
        newOptions[optIndex] = { ...newOptions[optIndex], ...updates };
        handleFieldChange({ options: newOptions });
    }, [fieldState.options, handleFieldChange]);

    return (
        <div className="bg-gray-100 dark:bg-gray-700 rounded-lg p-4 mb-4 relative">

            <div className="space-y-4">
                <div className="flex">

                    <textarea
                        className="w-full p-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 h-auto"
                        placeholder="Question"
                        value={fieldState.label}
                        onChange={(e) => handleFieldChange({ label: e.target.value })}
                    />
                    <div className="flex justify-end mb-2">
                        <div className="relative">
                            <Button
                                className="p-2 ml-1 text-gray-600 dark:text-gray-300 rounded-lg"
                                onClick={() => setIsMenuOpen(!isMenuOpen)}
                            >
                                <MoreVertical size={16}/>
                            </Button>
                            {isMenuOpen && (
                                <>
                                    <div
                                        className="fixed inset-0 z-10"
                                        onClick={() => setIsMenuOpen(false)}
                                    />
                                    <div className="absolute right-0 mt-1 py-1 w-48 bg-white dark:bg-gray-700 rounded-lg shadow-lg z-20">
                                        <button
                                            className="w-full px-4 py-2 text-left text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-600 flex items-center"
                                            onClick={() => {
                                                onDeleteField(field.id);
                                                setIsMenuOpen(false);
                                            }}
                                        >
                                            <Trash2 size={16} className="mr-2"/>
                                            Delete Field
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>

                </div>


                <div className="flex space-x-4">
                    <input
                        type="text"
                        className="flex-1 p-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        placeholder="Field name"
                        value={fieldState.name}
                        onChange={(e) => handleFieldChange({ name: e.target.value })}
                    />

                    <select
                        className="flex-1 p-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        value={fieldState.type}
                        onChange={(e) => {
                            const newType = e.target.value;
                            setShowOptions(['select', 'radio', 'checkbox'].includes(newType));
                            handleFieldChange({ type: newType });
                        }}
                    >
                        <option value="">Select Field Type</option>
                        {['header', 'paragraph', 'text', 'textarea', 'email', 'select', 'radio', 'checkbox']
                            .map(type => (
                                <option key={type} value={type} className="capitalize">
                                    {type}
                                </option>
                            ))}
                    </select>

                    {/* New block for up/down arrows + numeric order */}
                    <div className="flex items-center space-x-2">
                        <label>Order</label>
                        <input
                            type="number"
                            className="w-16 p-2 text-center border rounded max-w-20"
                            value={index + 1}
                            onChange={(e) => {
                                const newIndex = parseInt(e.target.value, 10) - 1;
                                if (!isNaN(newIndex)) {
                                    // We use a special 'toIndex' direction so we know
                                    // we want to move the field to a specific index
                                    onMoveField(index, 'toIndex', newIndex);
                                }
                            }}
                        />
                        <button
                            className="p-2 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg"
                            onClick={() => onMoveField(index, 'up')}
                            disabled={isFirst}
                        >
                            <ChevronUp size={16}/>
                        </button>
                        <button
                            className="p-2 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg"
                            onClick={() => onMoveField(index, 'down')}
                            disabled={isLast}
                        >
                            <ChevronDown size={16}/>
                        </button>
                        {/* The new order input */}

                    </div>


                </div>

                {showOptions && (
                    <div className="ml-6 space-y-2">
                        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Options</h4>
                        {fieldState.options?.map((option, optIndex) => (
                            <div key={optIndex} className="flex space-x-2">
                                <input
                                    type="text"
                                    className="flex-1 p-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                                    placeholder="Option value"
                                    value={option.value}
                                    onChange={(e) => updateOption(optIndex, { value: e.target.value })}
                                />
                                <input
                                    type="text"
                                    className="flex-1 p-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                                    placeholder="Option label"
                                    value={option.label}
                                    onChange={(e) => updateOption(optIndex, { label: e.target.value })}
                                />
                                <button
                                    className="p-2 text-red-600 hover:bg-red-100 dark:hover:bg-red-900 rounded-lg"
                                    onClick={() => removeOption(optIndex, option.id)}
                                >
                                    <Trash2 size={16}/>
                                </button>
                            </div>
                        ))}
                        <button
                            className="mt-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 dark:bg-blue-600 dark:hover:bg-blue-700"
                            onClick={addOption}
                        >
                            Add Option
                        </button>
                    </div>
                )}

                <div className="ml-6 space-y-2">
                    <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Required Documents</h4>
                    {fieldState.documents?.map((doc, docIndex) => (
                        <div key={docIndex} className="flex space-x-2">
                            <select
                                className="flex-1 p-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-indigo-300 text-gray-900 dark:text-gray-100 dark:bg-indigo-500"
                                value={doc.id || ''}
                                onChange={(e) => updateDocument(docIndex, e.target.value, fieldState)}
                            >
                                <option value="">Select Document Type</option>
                                {documentTypes.map(docType => (
                                    <option key={docType.id} value={docType.id}>
                                        {docType.name}
                                    </option>
                                ))}
                            </select>
                            <button
                                className="p-2 text-red-600 hover:bg-red-100 dark:hover:bg-red-900 rounded-lg"
                                onClick={() => removeDocument(docIndex, doc.id, fieldState)}
                            >
                                <Trash2 size={16}/>
                            </button>
                        </div>
                    ))}
                    <button
                        className="mt-2 px-4 py-2 text-white rounded-lg bg-gray-800 hover:bg-gray-700 dark:hover:bg-gray-600 rounded-lg"
                        onClick={addDocument}
                    >
                        Add Required Document
                    </button>
                </div>
            </div>
        </div>
    );
});

FormField.displayName = 'FormField';

const FormBuilder = ({ handleShowHeader, handleShowSettingsHeader }) => {
    const { id } = useParams();
    const { getFormBuilder, duplicateForm, updateFormBuilder, deleteFormBuilder } = useApi();
    const [forms, setForms] = useState([]);
    const [fieldCount, setFieldCount] = useState(0);
    const [deletedFields, setDeletedFields] = useState([]);
    const [currentForm, setCurrentForm] = useState(null);
    const [formData, setFormData] = useState(null);
    const [formName, setFormName] = useState('');
    const [formYear, setFormYear] = useState('');
    const [formType, setFormType] = useState('personal');
    const [isLoading, setIsLoading] = useState(false);
    const [documentTypes, setDocumentTypes] = useState([]);
    const [showDuplicateOptions, setShowDuplicateOptions] = useState(false);
    const [formToDuplicate, setFormToDuplicate] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [deletingForms, setDeletingForms] = useState({});

    const navigate = useNavigate();

    useEffect(() => {
        handleShowHeader(true);
        handleShowSettingsHeader(false);
        initialize();
    }, [id]);

    const initialize = async () => {
        setIsLoading(true);
        try {
            const response = await getFormBuilder(id);
            setForms(response.forms || []);
            setDocumentTypes(response.documentTypes || []);
            if (response.form) {
                setFormData(response.form);
                setCurrentForm(response.form.fields || []);
                setFormName(response.form.name || '');
                setFormYear(response.form.year || '');
                setFormType(response.form.type || 'personal');
                setFieldCount(response.form.fields?.length || 0);
            }
        }
        catch (error) {
            console.error('Error loading form data:', error);
        }
        finally {
            setIsLoading(false);
        }
    };

    const getFormToDuplicate = async (id) => {
        const response = await getFormBuilder(id);
        setForms(response.forms || []);
        setDocumentTypes(response.documentTypes || []);
        if (response.form) {
            response.form.id = null;
            setFormData(response.form);
            setCurrentForm(response.form.fields || []);
            setFormName(response.form.name || '');
            setFormYear(response.form.year || '');
            setFieldCount(response.form.fields?.length || 0);
        }
    }

    const handleFormDuplication = (formToDuplicateId) => {
        console.log(formToDuplicateId);
        let formToDuplicate = null;
        forms.forEach((form) => {
            console.log(form);
            if (form.id == formToDuplicateId) {
                formToDuplicate = form;
            }
        });

        console.log(formToDuplicate);

        setFormToDuplicate(formToDuplicate);

    }

    const saveDuplicateForm = async () => {
        const data = {
            form_name: formName,
            form_year: formYear,
            form_type: formType,
        }
        const response = await duplicateForm(formToDuplicate.id, data);

        if (response.form) {
            setFormData(response.form);
            setCurrentForm(response.form.fields || []);
            setFormName(response.form.name || '');
            setFormYear(response.form.year || '');
            setFormType(response.form.type || 'personal');
            setFieldCount(response.form.fields?.length || 0);

            console.log(currentForm);
            navigate(`/tax-questionnaire-builder/${response.form.id}`);
        }
    }

    const deleteForm = async (id) => {
        setDeletingForms(prev => ({ ...prev, [id]: true }));
        const response = await deleteFormBuilder(id);
        setDeletingForms(prev => ({ ...prev, [id]: false }));
        initialize();
    }

    const createNewForm = () => {
        // Set up empty form data for a new form
        setFormData({ id: null, name: formName, year: formYear, type: formType });
        setCurrentForm([]);  // Empty fields array
        setFieldCount(0);
        setDeletedFields([]);
        setShowDuplicateOptions(false);
    };

    const saveForm = async () => {
        const data = {
            form_id: formData?.id || null,  // null for new forms
            form_name: formName,
            form_year: formYear,
            form_type: formType,
            fields: currentForm.map((field, index) => ({
                ...field,
                order: index,
            })),
            deletedFields
        };

        console.log(data);

        setIsLoading(true);
        const response = await updateFormBuilder(formData?.id, data);
        setCurrentForm(response.form.fields);
        setFormData(response.form);
        setFormName(response.form.name || '');
        setFormYear(response.form.year || '');
        setFormType(response.form.type || 'personal');
        setIsLoading(false);
        navigate(`/tax-questionnaire-builder/${response.form.id}`)
    }

    const addField = useCallback(() => {
        setFieldCount(prev => prev + 1);
        setCurrentForm(prev => [
            ...(prev || []),
            {
                id: `temp-${fieldCount}`,
                label: '',
                name: '',
                type: '',
                order: fieldCount,
                options: [],
                documents: []
            }
        ]);
    }, [fieldCount]);

    const deleteField = useCallback((fieldId) => {
        if (!fieldId.toString().startsWith('temp-')) {
            setDeletedFields(prev => [...prev, fieldId]);
        }
        setCurrentForm(prev => prev.filter(field => field.id !== fieldId));
    }, []);

    const moveField = useCallback((index, direction, targetIndex) => {
        setCurrentForm(prev => {
            if (!prev) return prev;
            const newFields = [...prev];

            if (direction === 'up' && index > 0) {
                const temp = newFields[index];
                newFields[index] = newFields[index - 1];
                newFields[index - 1] = temp;
                return newFields;
            }

            if (direction === 'down' && index < newFields.length - 1) {
                const temp = newFields[index];
                newFields[index] = newFields[index + 1];
                newFields[index + 1] = temp;
                return newFields;
            }

            if (direction === 'toIndex') {
                // Make sure targetIndex is within range
                if (targetIndex < 0 || targetIndex >= newFields.length) {
                    return newFields; // do nothing if out of range
                }

                // Remove the field from the current position
                const [fieldToMove] = newFields.splice(index, 1);

                // Insert it at the new position
                newFields.splice(targetIndex, 0, fieldToMove);

                return newFields;
            }

            return newFields;
        });
    }, []);

    const updateField = useCallback((index, updatedField) => {
        setCurrentForm(prev => {
            const newFields = [...prev];
            newFields[index] = updatedField;
            return newFields;
        });
    }, []);

    if (isLoading) {
        return <LoadingScreen/>;
    }

    return (
        <div className="max-w-4xl mx-auto p-6 page-container relative z-10">
            <div className="space-y-6">
                <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm">
                    <h2 className="text-2xl font-bold mb-6 text-gray-900 dark:text-gray-100">
                        Tax Questionnaire Builder
                    </h2>

                    <div className="space-y-4">
                        <div className="flex space-x-4">
                            <input
                                type="text"
                                className="flex-1 p-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                                placeholder="Form Name"
                                value={formName}
                                onChange={(e) => setFormName(e.target.value)}
                            />
                            <input
                                type="text"
                                className="flex-1 p-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                                placeholder="Form Year (2024, 2025, etc)"
                                value={formYear}
                                onChange={(e) => setFormYear(e.target.value)}
                            />
                            <select
                                className="flex-1 p-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                                value={formType}
                                onChange={(e) => setFormType(e.target.value)}
                            >
                                <option value="personal">Personal</option>
                                <option value="business">Business</option>
                            </select>
                        </div>

                        <div className="space-y-2">
                            {!currentForm && (
                                <div className="space-y-2">
                                    <div className="flex space-x-4">
                                        {/* Create New Form Button */}
                                        <button
                                            disabled={formName.length <= 0 || formYear.length < 4}
                                            className={`px-4 py-2 text-white rounded-lg
                                                ${formName.length <= 0 || formYear.length < 4
                                                    ? 'bg-gray-400'
                                                    : 'bg-green-500 hover:bg-green-600 dark:bg-green-600 dark:hover:bg-green-700'}`}
                                            onClick={createNewForm}
                                        >
                                            Create New Form
                                        </button>

                                        {/* Duplicate Existing Form Button */}
                                        <button
                                            disabled={formName.length <= 0 || formYear.length < 4}
                                            className={`px-4 py-2 text-white rounded-lg
                                                ${formName.length <= 0 || formYear.length < 4
                                                    ? 'bg-gray-400'
                                                    : 'bg-blue-500 hover:bg-blue-600 dark:bg-blue-600 dark:hover:bg-blue-700'}`}
                                            onClick={() => setShowDuplicateOptions(!showDuplicateOptions)}
                                        >
                                            Duplicate Existing Form
                                        </button>
                                    </div>

                                    {/* Hint text when name/year not entered */}
                                    {(formName.length <= 0 || formYear.length < 4) && (
                                        <p className="text-sm text-gray-500 dark:text-gray-400">
                                            Enter form name and year to create or duplicate a form
                                        </p>
                                    )}
                                </div>
                            )}


                            {showDuplicateOptions && (
                                <>
                                    <select
                                        className="w-full p-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                                        value={formToDuplicate.id}
                                        onChange={(e) => handleFormDuplication(e.target.value)}
                                    >
                                        <option value="">Select a form to duplicate</option>
                                        {/* Add your existing forms here */}
                                        {forms.map((form) => (
                                            <option value={form.id} key={form.id}>{form.name || 'Untitled Form'} - {form.year || 'No Year'} ({form.type || 'personal'})</option>
                                        ))}
                                    </select>
                                    <button
                                        className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 dark:bg-blue-600 dark:hover:bg-blue-700"
                                        onClick={saveDuplicateForm}
                                    >
                                        Duplicate Form?
                                    </button>
                                </>

                            )}


                        </div>

                        {!currentForm && (
                            <div className="space-y-4">
                                <h3 className="text-lg font-semibold">Existing Forms</h3>
                                <div className="grid gap-4">
                                    {forms.map((form) => (
                                        <div key={form.id} className="flex w-full flex-row space-x-4">
                                            <Link to={`/tax-questionnaire-builder/${form.id}`}>
                                                <Button className="w-full text-left">
                                                    {form.name || 'Untitled Form'} ({form.year || 'No Year'}) - <span className="capitalize">{form.type || 'personal'}</span>
                                                </Button>
                                            </Link>
                                            {form.hasAnswers < 1 && (
                                                deletingForms[form.id] ? (
                                                    <span className={`flex flex-row gap-2 items-center`}>Deleting... <Loader2 className="w-4 h-4 animate-spin"/></span>
                                                ) : (
                                                    <button onClick={() => deleteForm(form.id)}>Delete</button>
                                                )
                                            )}
                                        </div>

                                    ))}
                                </div>
                            </div>
                        )}

                        {currentForm && (
                            <>
                                <div className="space-y-4">
                                    {currentForm.map((field, index) => (
                                        <FormField
                                            key={field.id}
                                            field={field}
                                            index={index}
                                            onFieldUpdate={updateField}
                                            onDeleteField={deleteField}
                                            onMoveField={moveField}
                                            isFirst={index === 0}
                                            isLast={index === currentForm.length - 1}
                                            documentTypes={documentTypes}
                                        />
                                    ))}
                                </div>

                                <div className="flex space-x-4">
                                    <button
                                        className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 dark:bg-green-600 dark:hover:bg-green-700"
                                        onClick={addField}
                                    >
                                        <Plus className="inline-block mr-2" size={16}/>
                                        Add Field
                                    </button>

                                    <button
                                        onClick={saveForm}
                                        disabled={isSaving}
                                        className={`
                                         px-6 py-2 rounded text-white
                                         ${isSaving ?
                                           'bg-blue-400 cursor-not-allowed animate-pulse' :
                                           'bg-blue-500 hover:bg-blue-600'
                                        }
                                         transition-colors duration-200
                                     `}
                                    >
                                        {isSaving ? 'Processing...' : 'Save Form'}
                                    </button>

                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FormBuilder;
